import { useEffect, useState } from "react";
import { Loader2, Handshake, UserRound, Building2, Home, CheckCircle2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
  useIsDesktop,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete } from "@/components/geo/AddressAutocomplete";
import { BrregSearch, formatOrgNumber } from "@/components/geo/BrregSearch";
import { geocodeAddress } from "@/lib/geo";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const EMPTY = {
  name: "",
  entity_type: "bedrift",
  customer_type: "engangskunde",
  org_number: "",
  contact_person: "",
  department: "",
  address: "",
  postal_code: "",
  city: "",
  lat: null,
  lng: null,
  address_source: null,
  address_approximate: false,
  phone: "",
  email: "",
  notes: "",
  brreg: null,
};

const TypeOption = ({ value, current, onSelect, icon: Icon, title, desc, testid, compact }) => {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={testid}
      aria-pressed={active}
      className={cn(
        "pressable flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        compact ? "min-h-[48px] items-center" : "min-h-[56px]",
        active ? "border-[#0B0D10] bg-[#0B0D10] text-white dark:border-white dark:bg-white dark:text-[#0B0D10]" : "border-border bg-card hover:bg-accent"
      )}
    >
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", active ? "bg-primary text-[#0B0D10]" : "bg-muted")}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        {desc && <span className={cn("block text-xs", active ? "opacity-75" : "text-muted-foreground")}>{desc}</span>}
      </span>
    </button>
  );
};

/**
 * CustomerDialog v2
 *  - Bedrift: søk i Brønnøysundregistera (namn / org.nr) → eksplisitt val fyller namn, org.nr og adresse. Manuelt er framleis mogleg.
 *  - Privat: vanleg manuell registrering (ingen org.nr).
 *  - Adresse med Kartverket-autocomplete → koordinatar til kartet.
 */
export const CustomerDialog = ({ open, onOpenChange, customer, onSaved }) => {
  const desktop = useIsDesktop();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null); // Brreg-treff som er brukt
  const [geoBusy, setGeoBusy] = useState(false);
  const isEdit = !!customer;

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    if (customer) {
      const next = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        if (customer[k] !== null && customer[k] !== undefined) next[k] = customer[k];
      });
      next.entity_type = customer.entity_type || "bedrift";
      next.customer_type = customer.customer_type || "engangskunde";
      setForm(next);
    } else {
      setForm(EMPTY);
    }
  }, [open, customer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPrivat = form.entity_type === "privat";

  const setAddress = (a) =>
    setForm((f) => ({
      ...f,
      address: a.address || "",
      postal_code: a.postal_code || "",
      city: a.city || "",
      lat: a.lat ?? null,
      lng: a.lng ?? null,
      address_source: a.source || null,
      address_approximate: !!a.approximate,
    }));

  // --- Brønnøysund: bruk treff (eksplisitt) ---
  const applyBrreg = async (r) => {
    if (r.existing_customer_id) {
      toast.info(`${r.name} er allereie registrert som kunde («${r.existing_customer_name}»).`);
    }
    setPicked(r);
    setForm((f) => ({
      ...f,
      name: r.name || f.name,
      org_number: r.org_number || "",
      address: r.address || f.address,
      postal_code: r.postal_code || "",
      city: r.city || "",
      lat: null,
      lng: null,
      address_source: r.address ? "brreg" : f.address_source,
      address_approximate: false,
      brreg: {
        org_form: r.org_form,
        org_form_desc: r.org_form_desc,
        industry: r.industry,
        employees: r.employees,
        website: r.website,
        municipality: r.municipality,
        bankrupt: r.bankrupt,
        fetched_at: new Date().toISOString(),
      },
    }));
    if (r.address) {
      setGeoBusy(true);
      const hit = await geocodeAddress(r.address);
      setGeoBusy(false);
      if (hit) {
        setForm((f) => ({ ...f, lat: hit.lat, lng: hit.lng, postal_code: f.postal_code || hit.postal_code || "", city: f.city || hit.city || "", address_approximate: !!hit.approximate }));
      }
    }
    toast.success(`Henta ${r.name} frå Brønnøysundregistera. Kontroller og fyll inn kontaktinfo.`);
  };

  const clearPicked = () => {
    setPicked(null);
    setForm((f) => ({ ...f, brreg: null }));
  };

  const switchEntity = (v) => {
    setForm((f) => ({ ...f, entity_type: v, ...(v === "privat" ? { org_number: "", brreg: null, department: "" } : {}) }));
    if (v === "privat") setPicked(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(isPrivat ? "Namn er påkrevd." : "Kundenamn er påkrevd.");
    if (!isPrivat && form.org_number && form.org_number.replace(/\D/g, "").length !== 9) {
      return toast.error("Org.nr må ha 9 siffer.");
    }
    setBusy(true);
    try {
      const payload = { ...form };
      ["machine_count", "id", "created_at", "updated_at", "created_by", "geocode_failed"].forEach((k) => delete payload[k]);
      if (isPrivat) {
        payload.org_number = null;
        payload.brreg = null;
      } else if (payload.org_number) {
        payload.org_number = payload.org_number.replace(/\D/g, "");
      } else {
        payload.org_number = null;
      }
      ["postal_code", "city", "contact_person", "department", "phone", "email", "notes"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (!payload.address) {
        payload.address = "";
        payload.lat = null;
        payload.lng = null;
        payload.address_source = null;
      }
      let saved;
      if (isEdit) saved = (await api.put(`/customers/${customer.id}`, payload)).data;
      else saved = (await api.post("/customers", payload)).data;
      toast.success(isEdit ? "Kunde oppdatert." : "Kunde oppretta.");
      if (payload.address && saved?.lat == null) {
        toast.info("Fann ikkje koordinatar for adressa – kunden blir ikkje vist på kartet før adressa er presisert.");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Noko gjekk gale.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} snapPoints={[0.94]}>
      <DialogContent className="flex max-h-[94vh] flex-col gap-0 p-0 sm:max-w-xl" drawerClassName="max-h-[94dvh]" data-testid="customer-dialog">
        <DialogHeader className={desktop ? "border-b px-6 pb-4 pt-6" : undefined}>
          <DialogTitle className="font-heading text-xl font-bold">{isEdit ? "Rediger kunde" : "Ny kunde"}</DialogTitle>
          <DialogDescription>
            {isPrivat ? "Privatkunde – fyll inn namn og kontaktinfo." : "Bedrift – søk opp firmaet i Brønnøysundregistera, eller fyll inn manuelt."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="contents" data-testid="customer-form">
          <DialogBody className={desktop ? "min-h-0 flex-1 content-start overflow-y-auto px-6 py-4" : undefined}>
            {/* Kundeslag */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Kundeslag</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="customer-entity-options">
                <TypeOption compact value="bedrift" current={form.entity_type} onSelect={switchEntity} icon={Building2} title="Bedrift" testid="customer-entity-bedrift" />
                <TypeOption compact value="privat" current={form.entity_type} onSelect={switchEntity} icon={Home} title="Privat" testid="customer-entity-privat" />
              </div>
            </div>

            {/* Brønnøysund */}
            {!isPrivat && (
              <div className="space-y-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3" data-testid="customer-brreg-panel">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="h-4 w-4 text-primary" /> Hent frå Brønnøysundregistera
                  <span className="ml-auto text-[11px] font-normal text-muted-foreground">valfritt</span>
                </div>
                {picked ? (
                  <div className="flex items-start gap-2.5 rounded-lg border bg-card p-2.5" data-testid="customer-brreg-picked">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--status-ok))]" />
                    <div className="min-w-0 flex-1 text-sm">
                      <div className="truncate font-semibold">{picked.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        Org.nr {formatOrgNumber(picked.org_number)}{picked.org_form_desc ? ` · ${picked.org_form_desc}` : ""}{picked.industry ? ` · ${picked.industry}` : ""}
                      </div>
                      {geoBusy && <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Finn koordinatar …</div>}
                    </div>
                    <button type="button" onClick={clearPicked} aria-label="Fjern Brønnøysund-treff" className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground" data-testid="customer-brreg-clear">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <BrregSearch onPick={applyBrreg} testid="customer-brreg" />
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="c-name" className="text-sm font-semibold">{isPrivat ? "Namn *" : "Kundenamn *"}</Label>
              <Input id="c-name" className="h-11" data-testid="customer-name-input" value={form.name} onChange={set("name")} placeholder={isPrivat ? "Ola Nordmann" : "BKS Anlegg AS"} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Avtaleform</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="customer-type-options">
                <TypeOption value="serviceavtale" current={form.customer_type} onSelect={(v) => setVal("customer_type", v)} icon={Handshake} title="Serviceavtale" desc="Fast oppfølging og planlagde fristar" testid="customer-type-serviceavtale" />
                <TypeOption value="engangskunde" current={form.customer_type} onSelect={(v) => setVal("customer_type", v)} icon={UserRound} title="Engangskunde" desc="Bestiller service ved behov" testid="customer-type-engangskunde" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="c-contact" className="text-sm font-semibold">{isPrivat ? "Kontaktperson (valfritt)" : "Kontaktperson"}</Label>
                <Input id="c-contact" className="h-11" data-testid="customer-contact-input" value={form.contact_person} onChange={set("contact_person")} />
              </div>
              {!isPrivat && (
                <div className="space-y-1.5">
                  <Label htmlFor="c-dep" className="text-sm font-semibold">Avdeling</Label>
                  <Input id="c-dep" className="h-11" value={form.department} onChange={set("department")} data-testid="customer-department-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="c-phone" className="text-sm font-semibold">Telefon</Label>
                <Input id="c-phone" className="h-11" type="tel" inputMode="tel" data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-email" className="text-sm font-semibold">E-post</Label>
                <Input id="c-email" className="h-11" type="email" inputMode="email" data-testid="customer-email-input" value={form.email} onChange={set("email")} />
              </div>
              {!isPrivat && (
                <div className="space-y-1.5">
                  <Label htmlFor="c-org" className="text-sm font-semibold">Org.nr</Label>
                  <Input id="c-org" className="h-11" inputMode="numeric" value={formatOrgNumber(form.org_number) || form.org_number} onChange={(e) => setVal("org_number", e.target.value.replace(/[^\d\s]/g, ""))} placeholder="937 694 644" data-testid="customer-org-input" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Adresse</Label>
              <AddressAutocomplete
                value={{ address: form.address, postal_code: form.postal_code, city: form.city, lat: form.lat, lng: form.lng, source: form.address_source, approximate: form.address_approximate }}
                onChange={setAddress}
                testid="customer-address"
                placeholder={isPrivat ? "Heimeadresse …" : "Forretnings-/anleggsadresse …"}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="c-notes" className="text-sm font-semibold">Notat</Label>
              <Textarea id="c-notes" rows={2} value={form.notes} onChange={set("notes")} data-testid="customer-notes-input" />
            </div>
          </DialogBody>
          <DialogFooter className={desktop ? "gap-2 border-t px-6 py-3 sm:gap-0" : undefined}>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="customer-cancel-button">Avbryt</Button>
            <Button type="submit" disabled={busy} data-testid="save-customer-button">
              {busy && <Loader2 className="animate-spin" />}
              {isEdit ? "Lagre endringar" : "Opprett kunde"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
